import { cn } from "@/lib/utils";

/**
 * FEAT-22 — how the code editor works, shown on the candidate's start page of
 * a test that has a coding question (`/preview` sends the text only then).
 *
 * The text is the workspace's own (Settings → Completion) or the platform
 * default, and it is PLAIN TEXT: rendered as React text nodes, never as HTML or
 * Markdown, so a recruiter-authored string cannot carry markup to a candidate.
 * A blank line starts a new paragraph; a single newline is kept as a line break.
 *
 * Shared with the Settings live preview, so that preview cannot drift from the
 * real screen (the CandidateDoneCard / CandidateTimeUpCard posture).
 */
export function CodeEditorInstructions({
  text,
  className,
}: {
  text: string | null | undefined;
  className?: string;
}) {
  const paragraphs = (text ?? "")
    .split(/\n[ \t]*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return null;
  return (
    <section
      aria-labelledby="code-editor-instructions-title"
      className={cn(
        "space-y-2 rounded-md border border-border bg-muted/30 p-4 text-sm",
        className,
      )}
    >
      <h3
        id="code-editor-instructions-title"
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        How the code editor works
      </h3>
      {paragraphs.map((paragraph, i) => (
        <p key={i} className="whitespace-pre-line text-sm text-foreground">
          {paragraph}
        </p>
      ))}
    </section>
  );
}
